import ExcelJS from 'exceljs'
import { saveAs } from 'file-saver'
import _ from 'lodash'
import moment from 'moment'
import { numberFormat, toBuddhistYear } from 'utils/app.utils'
import { forceTextOnDateCells } from 'utils/excel.utils'

export interface LabOpreativeReport {
    title: string
    statusFilter: string
    data: { rowDetail: any[]; rowDf: any[]; rowLab: any[] }
    snType: boolean
    t: any
}

export async function exportLabOpreativeToExcelDay(report: LabOpreativeReport) {
    const wb = new ExcelJS.Workbook()
    const ws = wb.addWorksheet(report.title, { pageSetup: { orientation: 'portrait' } })
    ws.properties.defaultRowHeight = 20

    // คอลัมน์ตามรายงาน (13 คอลัมน์)
    ws.columns = [
        { width: 22 },  // ลำดับ
        { width: 15 }, // รหัสผู้ป่วย
        { width: 15 }, // รหัสผู้ป่วยในสาขา
        { width: 25 }, // ชื่อผู้ป่วย
        { width: 25 }, // ประเภทการรักษา
        { width: 40 }, // รายการรักษา
        { width: 10 }, // LAB
        { width: 15 }, // ราคาหัตถการ
        { width: 12 }, // ค่า DF (เปอร์เซ็นต์)
        { width: 12 }, // DF
        { width: 15 }, // ยอดหักเครดิต
        { width: 15 }, // ยอดหลังคิด DF
        { width: 15 }  // DF สุทธิ
    ]

    let rowIndex = 1

    // แทรก Title (merge ทั้ง row)
    ws.mergeCells(rowIndex, 1, rowIndex, ws.columns.length)
    const titleCell = ws.getCell(rowIndex, 1)
    titleCell.value = report.title
    titleCell.font = { size: 16, bold: true }
    titleCell.alignment = { horizontal: 'center', vertical: 'middle' }
    rowIndex += 2

    // แทรก Status Filter (merge ทั้ง row)
    ws.mergeCells(rowIndex, 1, rowIndex, ws.columns.length)
    const statusCell = ws.getCell(rowIndex, 1)
    statusCell.value = report.statusFilter
    statusCell.font = { size: 12 }
    statusCell.alignment = { horizontal: 'left', vertical: 'middle' }
    rowIndex += 2

    // แยกข้อมูลตามหมอ (doctorId) กับข้อมูล DF และ LAB
    const groupedDoctors = !_.isEmpty(report.data)
        ? _.chain([
            ...report.data.rowDetail,
            ...report.data.rowDf,
            ...report.data.rowLab
        ])
            .groupBy('doctorId')
            .map((items: any[], doctorId: string) => {
                // หาข้อมูลหมอใน rowDetail หรือ group แรกๆ
                const doctorInfo = _.find(report.data.rowDetail, { doctorId: Number(doctorId) }) || items[0]

                return {
                    doctorFullname: doctorInfo?.doctorFullname || '-',
                    timeStart: doctorInfo?.timeStart || '-',
                    timeEnd: doctorInfo?.timeEnd || '-',
                    countAppointment: doctorInfo?.countAppointment || 0,
                    countTreatment: doctorInfo?.countTreatment || 0,
                    handInsurance: doctorInfo?.handInsurance || 0,
                    df: _.filter(items, v => _.has(v, 'operativeName') && !_.has(v, 'lab')),
                    lab: _.filter(items, v => _.has(v, 'lab')),
                }
            })
            .value()
        : []

    if (groupedDoctors.length === 0) {
        // ไม่มีข้อมูล
        const noDataRow = ws.addRow([`${report.t('REPORT.TITLE.NO_DATA')}`])
        ws.mergeCells(noDataRow.number, 1, noDataRow.number, ws.columns.length)
        noDataRow.font = { italic: true }
        noDataRow.alignment = { horizontal: 'center' }
    }

    const renderTimeWorking = (timeStart: string, timeEnd: string) => {
        if (timeStart !== '-' && timeEnd !== '-') return `${moment(timeStart, 'HH:mm').format('HH:mm')} - ${moment(timeEnd, 'HH:mm').format('HH:mm')} ${report.t('TIME_UNIT')}`
        else return '-'
    }
    for (const docGroup of groupedDoctors) {
        ws.addRow([`${report.t('REPORT.FILTER.DOCTOR')} : ${docGroup.doctorFullname}`])
        ws.addRow([
            `${report.t('REPORT.TITLE.TME_WORK')} : ${renderTimeWorking(docGroup.timeStart, docGroup.timeEnd)}`,
            `${report.t('REPORT.TITLE.COUNT_APPOINTMENT')} : ${docGroup.countAppointment}`,
            `${report.t('REPORT.TITLE.COUNT_TREATMENT')} : ${docGroup.countTreatment}`
        ])
        rowIndex = ws.lastRow ? ws.lastRow.number + 2 : rowIndex + 3

        // แทรก header ตาราง DF
        const dfHeaders = [
            report.t('TABLE.NO'),
            report.t('REPORT.TABLE.DF.CN'),
            report.t('REPORT.TABLE.DF.BRANCH_CN'),
            report.t('REPORT.TABLE.DF.SN_CODE'),
            report.t('REPORT.TABLE.DF.PATIENT_NAME'),
            report.t('REPORT.TABLE.DF.OPERATIVE_TYPE'),
            report.t('REPORT.TABLE.DF.OPERATIVE_LIST'),
            report.t('REPORT.TABLE.DF.IS_LAB'),
            report.t('REPORT.TABLE.DF.OPERATIVE_PRICE'),
            report.t('REPORT.TABLE.DF.DF'),
            report.t('REPORT.TABLE.DF.DF2'),
            report.t('REPORT.TABLE.DF.CREDIT_NET'),
            report.t('REPORT.TABLE.DF.TREATMENT_NET'),
            report.t('REPORT.TABLE.DF.DF3')
        ]

        if (report.snType) dfHeaders.splice(3, 1)

        ws.addRow(dfHeaders)
        rowIndex = ws.lastRow ? ws.lastRow.number + 1 : rowIndex + 1
        const headerRow = ws.getRow(rowIndex - 1)
        headerRow.font = { bold: true }
        headerRow.alignment = { horizontal: 'center' }

        // เติมข้อมูล DF หากไม่มี ให้แสดงแถว ไม่พบข้อมูล
        if (docGroup.df.length === 0) {
            const noDfRow = ws.addRow([`${report.t('REPORT.TITLE.NO_DATA')}`])
            ws.mergeCells(noDfRow.number, 1, noDfRow.number, dfHeaders.length)
            noDfRow.font = { italic: true }
            noDfRow.alignment = { horizontal: 'center' }
            rowIndex = noDfRow.number + 1
        } else {
            // เติมข้อมูล DF ตามลำดับ
            docGroup.df.forEach((item: any, idx: number) => {

                const dataDf = [
                    idx + 1,
                    item.cnNumber || '-',
                    item.branchCnNumber || '-',
                    item.patientFullname || '-',
                    item.patientFullname || '-',
                    item.operativeTypeName || '-',
                    item.operativeName || '-',
                    item.isLab ? report.t('REPORT.UNIT.HAS') : report.t('REPORT.UNIT.NOT_HAS'),
                    numberFormat(item.total || 0),
                    item.dfPercent != null ? `${item.dfPercent} %` : '0 %',
                    numberFormat(item.df || 0),
                    numberFormat(item.creditCost || 0),
                    numberFormat(item.dfNetBefore || 0),
                    numberFormat(item.dfNet || 0, 3, 3),
                ]

                if (report.snType) dataDf.splice(3, 1)

                const rowDf = ws.addRow(dataDf)
                rowDf.alignment = { vertical: 'middle' }
                const right = report.snType ? [8, 9, 10, 11, 12, 13] : [7, 8, 9, 10, 11, 12]
                rowDf.eachCell((cell, col) => {
                    cell.border = { top: { style: 'thin' }, left: { style: 'thin' }, bottom: { style: 'thin' }, right: { style: 'thin' } }
                    if ([1, report.snType ? 7 : 6].includes(col)) cell.alignment = { horizontal: 'center', vertical: 'middle' }
                    else if (right.includes(col)) cell.alignment = { horizontal: 'right', vertical: 'middle' }
                    else cell.alignment = { horizontal: 'left', vertical: 'middle' }
                })
            })
            rowIndex = ws.lastRow ? ws.lastRow.number + 1 : rowIndex + 1

            // เติมแถวรวมค่า (Sum)
            const sumRow = ws.addRow([
                'รวม',
                '',
                '',
                '',
                '',
                '',
                '',
                numberFormat(_.sumBy(docGroup.df, 'total')),
                '', // ค่า DF (เปอร์เซ็นต์) ไม่มีรวม
                numberFormat(_.sumBy(docGroup.df, 'df')),
                numberFormat(_.sumBy(docGroup.df, 'creditCost')),
                numberFormat(_.sumBy(docGroup.df, 'treatmentRight')),
                numberFormat(_.sumBy(docGroup.df, 'dfNet') || 0, 3, 3),
            ])
            const sumRowNumber = sumRow.number
            ws.mergeCells(sumRowNumber, 1, sumRowNumber, report.snType ? 7 : 6)
            sumRow.font = { bold: true }
            sumRow.alignment = { horizontal: 'right', vertical: 'middle' }
            sumRow.eachCell(cell => {
                cell.border = { top: { style: 'thin' }, left: { style: 'thin' }, bottom: { style: 'thin' }, right: { style: 'thin' } }
            })
            rowIndex = ws.lastRow ? ws.lastRow.number + 2 : rowIndex + 2
        }

        // แทรกตาราง LAB
        ws.addRow([])
        const labHeaders = [
            report.t('TABLE.NO'),
            report.t('REPORT.TABLE.DF_LAB.PATIENT_CN_NUMBER'),
            report.t('REPORT.TABLE.DF_LAB.SN_CODE'),
            report.t('REPORT.TABLE.DF_LAB.PATIENT_NAME'),
            report.t('REPORT.TABLE.DF_LAB.TX_LAB'),
            report.t('REPORT.TABLE.DF_LAB.LAB_LIST'),
            report.t('REPORT.TABLE.DF_LAB.LAB_REMARK'),
            report.t('REPORT.TABLE.DF_LAB.DATE_BILL'),
            report.t('REPORT.TABLE.DF_LAB.PRICE'),
            report.t('REPORT.TABLE.DF_LAB.PRICE_LAB')
        ]
        ws.addRow(labHeaders)
        const lastRowNumber = ws.lastRow?.number
        if (!lastRowNumber) { return }
        const labHeaderRow = ws.getRow(lastRowNumber)
        labHeaderRow.font = { bold: true }
        labHeaderRow.alignment = { horizontal: 'center' }
        labHeaderRow.eachCell(cell => {
            cell.border = {
                top: { style: 'thin' },
                left: { style: 'thin' },
                bottom: { style: 'thin' },
                right: { style: 'thin' }
            }
        })

        rowIndex = ws.lastRow ? ws.lastRow.number + 1 : rowIndex + 1

        if (docGroup.lab.length === 0) {
            const noLabRow = ws.addRow([`${report.t('REPORT.TITLE.NO_DATA')}`])
            ws.mergeCells(noLabRow.number, 1, noLabRow.number, labHeaders.length)
            noLabRow.font = { italic: true }
            noLabRow.alignment = { horizontal: 'center' }
            rowIndex = noLabRow.number + 1
        } else {
            docGroup.lab.forEach((labItem: any, idx: number) => {
                const rowLab = ws.addRow([
                    idx + 1,
                    labItem.cnNumber || '-',
                    labItem.patientFullname || '-',
                    labItem.operativeName || '-',
                    labItem.labFormType || '-',
                    labItem.labRemark || '-',
                    labItem.dateBill ? toBuddhistYear(moment(labItem.dateBill), 'DD/MM/YYYY') : '-',
                    numberFormat(labItem.labOrderPrice || 0),
                    numberFormat(labItem.labCost || 0)
                ])
                rowLab.alignment = { vertical: 'middle' }
                rowLab.eachCell((cell, col) => {
                    cell.border = { top: { style: 'thin' }, left: { style: 'thin' }, bottom: { style: 'thin' }, right: { style: 'thin' } }
                    if ([1].includes(col)) cell.alignment = { horizontal: 'center', vertical: 'middle' }
                    else if ([8, 9].includes(col)) cell.alignment = { horizontal: 'right', vertical: 'middle' }
                    else cell.alignment = { horizontal: 'left', vertical: 'middle' }
                })
            })
            rowIndex = ws.lastRow ? ws.lastRow.number + 1 : rowIndex + 1
        }

        // สรุปท้ายหมอ
        ws.addRow([])
        const summaryLabels = ['DF สุทธิ', 'ตัด LAB', 'ค่าประกันลงเวร', 'ยอดสรุป']
        const sumDfNet = _.sumBy(docGroup.df, 'dfNet') || 0
        const sumLabCost = _.sumBy(docGroup.lab, 'labCost') || 0
        const handInsurance = docGroup.handInsurance || 0
        const totalSummary = sumDfNet - sumLabCost + handInsurance

        for (let i = 0; i < summaryLabels.length; i++) {
            const label = summaryLabels[i]
            let val = 0
            if (label === 'DF สุทธิ') val = sumDfNet
            else if (label === 'ตัด LAB') val = sumLabCost
            else if (label === 'ค่าประกันลงเวร') val = handInsurance
            else if (label === 'ยอดสรุป') val = totalSummary

            const row = ws.addRow([label, numberFormat(val), 'บาท'])
            row.alignment = { vertical: 'middle' }
            row.getCell(1).font = { bold: true }
            row.getCell(2).alignment = { horizontal: 'right' }
        }

        rowIndex = ws.lastRow ? ws.lastRow.number + 2 : rowIndex + 6
    }

    // สร้างไฟล์ Excel และดาวน์โหลด
    forceTextOnDateCells(wb)
    const buffer = await wb.xlsx.writeBuffer()
    const filename = `${report.title}_${moment().format('YYYYMMDD')}.xlsx`
    saveAs(new Blob([buffer]), filename)
}
